import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import BulkRegisterModal from './BulkRegisterModal';
import BulkRegisterWorkshopModal from './BulkRegisterWorkshopModal';

const PAGE_SIZE = 25;

const Pagination = ({ currentPage, totalPages, onPageChange, totalItems, pageSize }) => {
    const from = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const to = Math.min(currentPage * pageSize, totalItems);
    const getPages = () => {
        const delta = 2, pages = [];
        for (let i = 1; i <= totalPages; i++) {
            if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) pages.push(i);
        }
        const items = []; let prev = null;
        for (const page of pages) { if (prev && page - prev > 1) items.push('...'); items.push(page); prev = page; }
        return items;
    };
    return (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 bg-white">
            <p className="text-xs text-gray-500">
                Showing <span className="font-semibold text-gray-700">{from}–{to}</span> of <span className="font-semibold text-gray-700">{totalItems}</span> results
                {totalPages > 1 && <span className="text-gray-400 ml-1">· Page {currentPage} of {totalPages}</span>}
            </p>
            {totalPages > 1 && (
                <div className="flex items-center gap-1">
                    <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}
                        className="px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">‹ Prev</button>
                    {getPages().map((item, i) => item === '...'
                        ? <span key={`e${i}`} className="w-8 text-center text-xs text-gray-400">…</span>
                        : <button key={item} onClick={() => onPageChange(item)}
                            className={`w-8 h-8 text-xs font-semibold rounded-md transition-colors ${currentPage === item ? 'bg-indigo-600 text-white' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'}`}>{item}</button>
                    )}
                    <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}
                        className="px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">Next ›</button>
                </div>
            )}
        </div>
    );
};

// ---- Edit Modal ----
const CONF_FIELDS = [
    { key: 'fullName', label: 'Full Name', type: 'text' },
    { key: 'email', label: 'Email', type: 'email' },
    { key: 'contactNumber', label: 'Contact Number', type: 'text' },
    { key: 'whatsappNumber', label: 'WhatsApp Number', type: 'text' },
    { key: 'organization', label: 'Organization / Institute', type: 'text' },
    { key: 'designation', label: 'Designation', type: 'text' },
    { key: 'city', label: 'City', type: 'text' },
    { key: 'state', label: 'State', type: 'text' },
    { key: 'registrationCategory', label: 'Registration Category', type: 'text' },
    { key: 'categoryIdNumber', label: 'Category Auth ID (Membership No.)', type: 'text' },
    { key: 'idNumberLabel', label: 'ID Number Label', type: 'text' },
    { key: 'foodChoice', label: 'Food Choice', type: 'text' },
    { key: 'accompanyingSpouse', label: 'Accompanying Spouse', type: 'text' },
    { key: 'transactionId', label: 'Transaction ID', type: 'text' },
    { key: 'paymentStatus', label: 'Payment Status', type: 'select', options: ['pending', 'completed', 'failed'] },
    { key: 'amountAmount', label: 'Amount (₹)', type: 'number' },
];

const WORKSHOP_FIELDS = [
    { key: 'fullName', label: 'Full Name', type: 'text' },
    { key: 'email', label: 'Email', type: 'email' },
    { key: 'contactNumber', label: 'Contact Number', type: 'text' },
    { key: 'whatsappNumber', label: 'WhatsApp Number', type: 'text' },
    { key: 'registrationCategory', label: 'Registration Category', type: 'text' },
    { key: 'conferenceRegId', label: 'Conference Reg ID', type: 'text' },
    { key: 'selectedWorkshopId', label: 'Workshop ID', type: 'text' },
    { key: 'transactionId', label: 'Transaction ID', type: 'text' },
    { key: 'paymentStatus', label: 'Payment Status', type: 'select', options: ['pending', 'completed', 'failed'] },
    { key: 'amountAmount', label: 'Amount (₹)', type: 'number' },
];

const EditModal = ({ participant, activeTab, credentials, onClose, onSaved }) => {
    const fields = activeTab === 'workshop' ? WORKSHOP_FIELDS : CONF_FIELDS;
    const [form, setForm] = useState(() => {
        const init = {};
        fields.forEach(f => { init[f.key] = participant[f.key] ?? ''; });
        return init;
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const handleChange = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

    const handleSave = async () => {
        setSaving(true); setError('');
        try {
            const endpoint = activeTab === 'workshop'
                ? `/api/custom-events/admin/workshop/participant/${participant._id}/edit`
                : `/api/custom-events/admin/participant/${participant._id}/edit`;
            const res = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...credentials, ...form })
            });
            const data = await res.json();
            if (data.success) { onSaved(data.data); }
            else setError(data.message || 'Update failed');
        } catch { setError('Error connecting to server'); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50 shrink-0">
                    <div>
                        <h2 className="text-base font-bold text-gray-900">Edit Participant</h2>
                        <p className="text-xs text-gray-500 mt-0.5 font-mono">{participant.registrationId}</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none">&times;</button>
                </div>

                {/* Body */}
                <div className="overflow-y-auto flex-1 px-6 py-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {fields.map(f => (
                            <div key={f.key} className={f.key === 'fullName' || f.key === 'email' ? 'sm:col-span-2' : ''}>
                                <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">{f.label}</label>
                                {f.type === 'select' ? (
                                    <select value={form[f.key]} onChange={e => handleChange(f.key, e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white">
                                        {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                                    </select>
                                ) : (
                                    <input type={f.type} value={form[f.key]} onChange={e => handleChange(f.key, e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
                                )}
                            </div>
                        ))}
                    </div>
                    {error && <p className="mt-4 text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg font-medium">{error}</p>}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50 shrink-0">
                    <p className="text-xs text-gray-400">Changes save directly to database</p>
                    <div className="flex gap-2">
                        <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">Cancel</button>
                        <button onClick={handleSave} disabled={saving}
                            className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 rounded-lg transition-colors">
                            {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ---- Main Component ----
const CustomEventAdminData = () => {
    const [authenticated, setAuthenticated] = useState(false);
    const [credentials, setCredentials] = useState({ username: '', password: '' });
    const [participants, setParticipants] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [adminRole, setAdminRole] = useState('');
    const [checkInLoading, setCheckInLoading] = useState(null);
    const [zipLoading, setZipLoading] = useState(false);
    const [resendEmailLoading, setResendEmailLoading] = useState(null);
    const [resendEmailMsg, setResendEmailMsg] = useState('');

    const urlParams = new URLSearchParams(window.location.search);
    const scannedRegId = urlParams.get('regId');

    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('');
    const [sortBy, setSortBy] = useState('dateDesc');
    const [currentPage, setCurrentPage] = useState(1);
    const [activeTab, setActiveTab] = useState('conference');

    // Edit modal
    const [editParticipant, setEditParticipant] = useState(null);
    const [showBulkRegister, setShowBulkRegister] = useState(false);
    const [showBulkRegisterWorkshop, setShowBulkRegisterWorkshop] = useState(false);

    useEffect(() => {
        const storedAuth = localStorage.getItem('kapconAdminAuth');
        if (storedAuth) {
            try {
                const parsed = JSON.parse(storedAuth);
                setCredentials({ username: parsed.username, password: parsed.password });
                setAdminRole(parsed.role || '');
                setAuthenticated(true);
            } catch (e) { console.error(e); }
        }
    }, []);

    useEffect(() => { if (authenticated) fetchParticipants(); }, [authenticated, activeTab]);
    useEffect(() => { setCurrentPage(1); }, [searchTerm, filterCategory, sortBy, activeTab]);

    const handleLoginChange = (e) => setCredentials(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const fetchParticipants = async () => {
        try {
            setLoading(true);
            const endpoint = activeTab === 'workshop'
                ? '/api/custom-events/admin/workshop/participants'
                : '/api/custom-events/admin/participants';
            const res = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(credentials)
            });
            const data = await res.json();
            if (data.success) { setParticipants(data.data || []); }
            else {
                if (res.status === 401) { setAuthenticated(false); localStorage.removeItem('kapconAdminAuth'); }
                setMessage(data.message || 'Error fetching data');
            }
        } catch { setMessage('Error connecting to server'); }
        finally { setLoading(false); }
    };

    const handleLogin = async (e) => {
        e.preventDefault(); setMessage(''); setLoading(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/custom-events/admin/login`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(credentials)
            });
            const data = await res.json();
            if (data.success) {
                localStorage.setItem('kapconAdminAuth', JSON.stringify({ username: credentials.username, password: credentials.password, role: data.role }));
                setAdminRole(data.role || ''); setAuthenticated(true);
            } else { setMessage(data.message || 'Invalid credentials'); localStorage.removeItem('kapconAdminAuth'); }
        } catch { setMessage('Error connecting to server'); }
        finally { setLoading(false); }
    };

    const handleLogout = () => { localStorage.removeItem('kapconAdminAuth'); setAuthenticated(false); setAdminRole(''); };

    const handleCheckInToggle = async (regId, currentStatus) => {
        if (!window.confirm(`Mark ${regId} as ${currentStatus ? 'NOT Checked In' : 'CHECKED IN'}?`)) return;
        try {
            setCheckInLoading(regId);
            const endpoint = activeTab === 'conference' ? '/api/custom-events/admin/checkin' : '/api/custom-events/admin/workshop/checkin';
            const res = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...credentials, regId, checkedIn: !currentStatus })
            });
            const data = await res.json();
            if (data.success) { setParticipants(prev => prev.map(p => p.registrationId === regId ? { ...p, isCheckedIn: !currentStatus } : p)); }
            else alert(data.message || 'Failed to update check-in status');
        } catch { alert('Error updating check-in status'); }
        finally { setCheckInLoading(null); }
    };

    const handleEditSaved = (updated) => {
        setParticipants(prev => prev.map(p => p._id === updated._id ? { ...p, ...updated } : p));
        setEditParticipant(null);
    };

    const handleResendEmail = async (participant) => {
        if (!window.confirm(`Resend email to ${participant.email}?`)) return;
        setResendEmailLoading(participant._id);
        setResendEmailMsg('');
        try {
            const endpoint = activeTab === 'workshop'
                ? `/api/custom-events/admin/workshop/participant/${participant._id}/resend-email`
                : `/api/custom-events/admin/participant/${participant._id}/resend-email`;
            const res = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(credentials)
            });
            const data = await res.json();
            setResendEmailMsg(data.success ? `✓ ${data.message}` : `✗ ${data.message || 'Failed'}`);
        } catch { setResendEmailMsg('✗ Error connecting to server'); }
        finally { setResendEmailLoading(null); setTimeout(() => setResendEmailMsg(''), 5000); }
    };

    const downloadZip = async () => {
        setZipLoading(true);
        try {
            const endpoint = activeTab === 'conference' ? '/api/custom-events/admin/download-tickets' : '/api/custom-events/admin/workshop/download-id-cards';
            const response = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(credentials)
            });
            if (!response.ok) { const err = await response.json(); throw new Error(err.message || 'Download failed'); }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none'; a.href = url;
            a.download = activeTab === 'conference' ? 'KAPCON_2026_MTickets.zip' : 'KAPCON_2026_Workshop10_IDCards.zip';
            document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url);
        } catch (err) { alert(err.message || 'Failed to download zip'); }
        finally { setZipLoading(false); }
    };

    if (!authenticated) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
                <div className="sm:mx-auto sm:w-full sm:max-w-md">
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">Admin Login for Participant Data</h2>
                </div>
                <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                    <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
                        <form className="space-y-6" onSubmit={handleLogin}>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Username</label>
                                <input name="username" type="text" required value={credentials.username} onChange={handleLoginChange}
                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#4013b5] focus:border-[#4013b5] sm:text-sm" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Password</label>
                                <input name="password" type="password" required value={credentials.password} onChange={handleLoginChange}
                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#4013b5] focus:border-[#4013b5] sm:text-sm" />
                            </div>
                            {message && <div className="text-red-600 text-sm">{message}</div>}
                            <button type="submit" disabled={loading}
                                className="w-full flex justify-center py-2 px-4 rounded-md text-sm font-medium text-white bg-[#4013b5] hover:bg-[#A02B00] disabled:bg-opacity-50">
                                {loading ? 'Logging in...' : 'Log in'}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        );
    }

    // Derived values
    const filteredParticipants = participants.filter(p => {
        const q = searchTerm.toLowerCase();
        const matchesSearch = !q ||
            (p.fullName || '').toLowerCase().includes(q) ||
            (p.email || '').toLowerCase().includes(q) ||
            (p.contactNumber || '').includes(q) ||
            (p.registrationId || '').toLowerCase().includes(q) ||
            (p.organization || '').toLowerCase().includes(q);
        const matchesCategory = filterCategory ? p.registrationCategory === filterCategory : true;
        return matchesSearch && matchesCategory;
    });

    const sortedParticipants = [...filteredParticipants].sort((a, b) => {
        if (sortBy === 'dateDesc') return new Date(b.createdAt) - new Date(a.createdAt);
        if (sortBy === 'dateAsc') return new Date(a.createdAt) - new Date(b.createdAt);
        if (sortBy === 'nameAsc') return (a.fullName || '').localeCompare(b.fullName || '');
        if (sortBy === 'amountDesc') return Number(b.amountAmount || 0) - Number(a.amountAmount || 0);
        return 0;
    });

    const totalPages = Math.ceil(sortedParticipants.length / PAGE_SIZE);
    const pagedParticipants = sortedParticipants.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    const totalCount = participants.length;
    const totalAmount = participants.filter(p => p.paymentStatus === 'completed').reduce((sum, p) => sum + Number(p.amountAmount || 0), 0);
    const categoryCounts = participants.reduce((acc, p) => { const cat = p.registrationCategory || 'Uncategorized'; acc[cat] = (acc[cat] || 0) + 1; return acc; }, {});
    const uniqueCategories = [...new Set(participants.map(p => p.registrationCategory).filter(Boolean))];

    const exportToExcel = () => {
        const exportData = sortedParticipants.map(p => ({
            'Reg ID': p.registrationId, 'Conf Reg ID': p.conferenceRegId || '', 'Name': p.fullName,
            'Email': p.email, 'Phone': p.contactNumber, 'WhatsApp': p.whatsappNumber,
            'Organization': p.organization || '', 'Designation': p.designation || '',
            'Category': p.registrationCategory, 'ID Number Label': p.idNumberLabel || 'Category Auth ID',
            'Category Auth ID': p.categoryIdNumber || '', 'Workshop ID': p.selectedWorkshopId || '',
            'Acc. Spouse': p.accompanyingSpouse || '', 'Amount': Number(p.amountAmount || 0),
            'Status': p.paymentStatus, 'Food Choice': p.foodChoice || '',
            'Checked In': p.isCheckedIn ? 'Yes' : 'No', 'Date': new Date(p.createdAt).toLocaleDateString()
        }));
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Participants');
        XLSX.writeFile(wb, 'KAPCON_2026_Participants.xlsx');
    };

    const ticketUrl = (regId) => activeTab === 'workshop'
        ? `/event/jss/workshop/participant/${regId}`
        : `/event/jss/conference/participant/${regId}`;

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto space-y-5">

                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                    <div className="flex flex-col gap-2">
                        <h1 className="text-2xl font-bold text-gray-900">KAPCON 2026 Participants</h1>
                        <div className="flex bg-white rounded-lg p-1 border shadow-sm w-max gap-1">
                            <button onClick={() => setActiveTab('conference')}
                                className={`px-4 py-1.5 text-sm font-bold rounded-md transition-colors ${activeTab === 'conference' ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:bg-gray-50'}`}>
                                Conference
                            </button>
                            <button onClick={() => setActiveTab('workshop')}
                                className={`px-4 py-1.5 text-sm font-bold rounded-md transition-colors ${activeTab === 'workshop' ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:bg-gray-50'}`}>
                                Workshop
                            </button>
                        </div>
                    </div>
                    <div className="flex gap-2 items-center flex-wrap">
                        <button onClick={fetchParticipants} className="px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md bg-white hover:bg-gray-50 text-gray-700">
                            {loading ? 'Refreshing...' : '↻ Refresh'}
                        </button>
                        <a href="/event/jss/admin/scan" className="px-3 py-1.5 border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded-md text-sm font-semibold">📷 Scan</a>
                        {adminRole === 'full' && activeTab === 'conference' && (
                            <button onClick={() => setShowBulkRegister(true)} className="px-3 py-1.5 border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-md text-sm font-semibold">
                                ⬆ Bulk Register
                            </button>
                        )}
                        {adminRole === 'full' && activeTab === 'workshop' && (
                            <button onClick={() => setShowBulkRegisterWorkshop(true)} className="px-3 py-1.5 border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 rounded-md text-sm font-semibold">
                                ⬆ Bulk Register
                            </button>
                        )}
                        {adminRole === 'full' && <a href="/event/jss/conference/admin" className="text-indigo-600 hover:text-indigo-900 text-sm font-medium">Config →</a>}
                        <button onClick={handleLogout} className="px-3 py-1.5 border border-red-300 text-red-700 bg-red-50 hover:bg-red-100 rounded-md text-sm font-semibold">Logout</button>
                    </div>
                </div>

                {message && <div className="p-3 rounded-md bg-red-50 text-red-800 text-sm">{message}</div>}
                {resendEmailMsg && (
                    <div className={`p-3 rounded-md text-sm font-medium ${resendEmailMsg.startsWith('✓') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                        {resendEmailMsg}
                    </div>
                )}

                {/* Stats Panel */}
                {adminRole === 'full' && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                            <p className="text-xs font-medium text-gray-500 mb-1">Total Registered</p>
                            <p className="text-3xl font-bold text-gray-900">{totalCount}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                            <p className="text-xs font-medium text-gray-500 mb-1">Total Amount (Completed)</p>
                            <p className="text-3xl font-bold text-emerald-600">₹{totalAmount.toLocaleString('en-IN')}</p>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                            <p className="text-xs font-medium text-gray-500 mb-2">Category Breakdown</p>
                            <div className="flex flex-wrap gap-1.5 max-h-20 overflow-y-auto">
                                {Object.entries(categoryCounts).map(([cat, count], i) => (
                                    <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-[#fcfaff] border border-[#f0d5c8] text-[#5928ed]">
                                        {cat || 'Unknown'}: {count}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Search / Filter / Export */}
                <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100 flex flex-col xl:flex-row gap-3 items-start xl:items-center justify-between">
                    <div className="flex flex-col sm:flex-row gap-2 w-full xl:w-auto flex-wrap">
                        <div className="relative w-full sm:w-72">
                            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 105 11a6 6 0 0012 0z" />
                            </svg>
                            <input type="text" placeholder="Search name, email, phone, reg ID..."
                                value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                                className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent" />
                            {searchTerm && <button onClick={() => setSearchTerm('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg leading-none">×</button>}
                        </div>
                        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
                            className="px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white">
                            <option value="">All Categories</option>
                            {uniqueCategories.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                            className="px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white">
                            <option value="dateDesc">Newest First</option>
                            <option value="dateAsc">Oldest First</option>
                            <option value="nameAsc">Name A–Z</option>
                            <option value="amountDesc">Amount Highest</option>
                        </select>
                    </div>
                    {adminRole === 'full' && (
                        <div className="flex gap-2 flex-wrap">
                            <button onClick={downloadZip} disabled={zipLoading}
                                className="inline-flex items-center px-3 py-2 text-sm font-semibold rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 transition-colors">
                                {zipLoading ? 'Zipping...' : activeTab === 'conference' ? '⬇ MTickets ZIP' : '⬇ ID Cards ZIP'}
                            </button>
                            <button onClick={exportToExcel}
                                className="inline-flex items-center px-3 py-2 text-sm font-semibold rounded-md text-white bg-emerald-600 hover:bg-emerald-700 transition-colors">
                                ⬇ Export Excel
                            </button>
                        </div>
                    )}
                </div>

                {/* Results info */}
                <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>{sortedParticipants.length === participants.length ? `${participants.length} total` : `${sortedParticipants.length} of ${participants.length} matching`}</span>
                    {searchTerm && <span className="bg-indigo-50 text-indigo-700 font-semibold px-2 py-0.5 rounded-full">"{searchTerm}"</span>}
                </div>

                {/* Table */}
                <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Reg ID / Actions</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Email / Phone</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Amount</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100">
                                {loading && pagedParticipants.length === 0 ? (
                                    <tr><td colSpan="7" className="px-4 py-10 text-center text-sm text-gray-400">Loading...</td></tr>
                                ) : pagedParticipants.length === 0 ? (
                                    <tr><td colSpan="7" className="px-4 py-10 text-center text-sm text-gray-400">
                                        {searchTerm ? 'No results match your search.' : 'No data found.'}
                                    </td></tr>
                                ) : pagedParticipants.map(p => (
                                    <tr key={p._id} className={`hover:bg-gray-50/60 transition-colors ${scannedRegId === p.registrationId ? 'bg-yellow-50 ring-2 ring-inset ring-yellow-400' : ''}`}>
                                        {/* Reg ID + Actions */}
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="text-xs font-bold font-mono text-gray-900 mb-1.5">{p.registrationId || '—'}</div>
                                            <div className="flex items-center gap-1 flex-wrap">
                                                {p.registrationId && (
                                                    <button onClick={() => handleCheckInToggle(p.registrationId, p.isCheckedIn)}
                                                        disabled={checkInLoading === p.registrationId}
                                                        className={`text-[10px] font-bold px-2 py-0.5 rounded-full transition-colors ${p.isCheckedIn ? 'bg-green-100 text-green-800 hover:bg-green-200 border border-green-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'}`}>
                                                        {checkInLoading === p.registrationId ? '...' : p.isCheckedIn ? '✓ IN' : 'CHECK IN'}
                                                    </button>
                                                )}
                                                {p.registrationId && (
                                                    <a href={ticketUrl(p.registrationId)} target="_blank" rel="noreferrer"
                                                        className="text-[10px] font-bold px-2 py-0.5 rounded-full border border-[#f0d5c8] bg-[#fcfaff] text-[#5928ed] hover:bg-[#5928ed] hover:text-white transition-colors">
                                                        🎫 Ticket
                                                    </a>
                                                )}
                                                {adminRole === 'full' && (
                                                    <button onClick={() => setEditParticipant(p)}
                                                        className="text-[10px] font-bold px-2 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors">
                                                        ✏ Edit
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => handleResendEmail(p)}
                                                    disabled={resendEmailLoading === p._id}
                                                    className="text-[10px] font-bold px-2 py-0.5 rounded-full border border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 disabled:opacity-50 transition-colors">
                                                    {resendEmailLoading === p._id ? '...' : '📧 Resend'}
                                                </button>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">{new Date(p.createdAt).toLocaleDateString()}</td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">{p.fullName}</div>
                                            <div className="text-xs text-gray-400">{p.organization}</div>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="text-xs text-gray-900">{p.email}</div>
                                            <div className="text-xs text-gray-500">{p.contactNumber}</div>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="text-xs font-semibold text-gray-700">{p.registrationCategory}</div>
                                            {p.categoryIdNumber && (
                                                <div className="text-[10px] text-indigo-600 mt-0.5 font-mono">{p.idNumberLabel || 'ID'}: {p.categoryIdNumber}</div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-xs font-semibold text-gray-900">
                                            ₹{Number(p.amountAmount || 0).toLocaleString('en-IN')}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <span className={`px-2 py-0.5 inline-flex text-[10px] font-bold rounded-full ${p.paymentStatus === 'completed' ? 'bg-green-100 text-green-800' : p.paymentStatus === 'failed' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                                {(p.paymentStatus || 'pending').toUpperCase()}
                                            </span>
                                            <div className="text-[10px] text-gray-400 font-mono mt-0.5 truncate max-w-[120px]">
                                                {p.razorpayPaymentId || p.razorpayOrderId || ''}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={sortedParticipants.length} pageSize={PAGE_SIZE} />
                </div>

            </div>

            {/* Edit Modal */}
            {editParticipant && (
                <EditModal
                    participant={editParticipant}
                    activeTab={activeTab}
                    credentials={credentials}
                    onClose={() => setEditParticipant(null)}
                    onSaved={handleEditSaved}
                />
            )}

            {/* Bulk Register Modal - Conference */}
            {showBulkRegister && (
                <BulkRegisterModal
                    credentials={credentials}
                    onClose={() => setShowBulkRegister(false)}
                    onImported={() => { setShowBulkRegister(false); fetchParticipants(); }}
                />
            )}

            {/* Bulk Register Modal - Workshop */}
            {showBulkRegisterWorkshop && (
                <BulkRegisterWorkshopModal
                    credentials={credentials}
                    onClose={() => setShowBulkRegisterWorkshop(false)}
                    onImported={() => { setShowBulkRegisterWorkshop(false); fetchParticipants(); }}
                />
            )}
        </div>
    );
};

export default CustomEventAdminData;
